import { Button, Col, Descriptions, Row, Space, Spin } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { Document, Page } from 'react-pdf';
import { MatchingEntity, DocumentReference, FileWithUrl } from '../../types';
import { FailedToLoadPage } from '../splitter/components';
import pdfjs from 'pdfjs-dist/webpack';
import { Spinner } from '../../components/loading/Spinner';

const WIDTH = 226;
const HEIGHT = 320;

type Props = {
  selectedMatching: MatchingEntity;
  setDetailMatching: any;
  documents: DocumentReference[];
  setDocuments: (documents: any) => any;
  numPages: { source: number; match: number };
  setNumPages: any;
  presignedUrls: FileWithUrl[];
};

export const Matching = ({
  presignedUrls,
  selectedMatching,
  setDetailMatching,
  documents,
  setDocuments,
  numPages,
  setNumPages,
}: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);

  function getBase64(file: any) {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  }

  const fetchPdfDocs = useCallback(async () => {
    if (selectedMatching) {
      try {
        setLoading(true);
        const filteredUrls = presignedUrls.filter(
          (file: FileWithUrl) => file.id === selectedMatching.id || file.id === selectedMatching.match_id
        );
        if (filteredUrls.length) {
          const newDocs = await Promise.all(
            filteredUrls.map(async fileWithUrl => {
              const document = await pdfjs.getDocument(fileWithUrl.fileUrl);
              const data = await (document as any).getData();
              const blob = new Blob([data as any], { type: 'application/pdf' });
              const base64 = await getBase64(blob);
              return {
                id: fileWithUrl.id,
                pdf: base64,
              };
            })
          );
          setDocuments((prev: any) => [...prev, ...newDocs]);
        }
        setError(false);
      } catch (e) {
        setError(true);
        setDocuments([]);
        console.log(e);
      } finally {
        setLoading(false);
      }
    }
  }, [selectedMatching, presignedUrls, setDocuments]);

  useEffect(() => {
    if (selectedMatching) {
      if (
        !(
          documents.find(file => file.id === selectedMatching.id) &&
          documents.find(file => file.id === selectedMatching.match_id)
        )
      ) {
        fetchPdfDocs();
      }
    }
  }, [selectedMatching, fetchPdfDocs, documents]);

  return (
    <>
      {loading && <Spinner />}
      {!loading && error && (
        <div className="flex justify-center items-center p-8">An error has occured while loading this file.</div>
      )}
      {!loading && !error && (
        <>
          <Row className="w-full" style={{ borderBottom: '1px solid #dcdcdc' }}>
            <Col xs={{ span: 16 }} xl={{ span: 18 }} className="flex justify-center items-center p-4">
              <Space>
                <div style={{ width: WIDTH, textAlign: 'center', fontWeight: 'bold' }}>{selectedMatching.id}</div>
                <div style={{ width: WIDTH, textAlign: 'center', fontWeight: 'bold' }}>{selectedMatching.match_id}</div>
              </Space>
            </Col>
            <Col
              xs={{ span: 8 }}
              xl={{ span: 6 }}
              style={{ background: '#fff', borderLeft: '1px solid #dcdcdc' }}
              className="flex justify-center items-center">
              <Button
                onClick={() => {
                  setDetailMatching({ entity: selectedMatching, page: 0 });
                }}>
                View pages in detail
              </Button>
            </Col>
          </Row>
          {selectedMatching.content.map(pageMatch => {
            return (
              <Row
                key={pageMatch.source_page}
                style={{
                  height: selectedMatching.content.length <= 2 ? '50%' : '',
                  minHeight: '350px',
                  width: '100%',
                }}>
                <Col
                  xs={{ span: 16 }}
                  xl={{ span: 18 }}
                  className="flex justify-center items-center"
                  style={{ borderBottom: '1px solid #cecece' }}>
                  <div className="relative" style={{ width: '100%', padding: '1em' }}>
                    <div
                      style={{
                        width: '100%',
                        minHeight: HEIGHT,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                      }}>
                      <Space size="large">
                        <div
                          style={{ width: WIDTH, cursor: 'pointer' }}
                          onClick={() => {
                            setDetailMatching({ entity: selectedMatching, page: pageMatch.source_page });
                          }}>
                          <Document
                            file={documents.find(file => file.id === selectedMatching.id)?.pdf}
                            loading={<Spin />}
                            onLoadSuccess={({ numPages: nextNumPages }: any) => {
                              setNumPages((prev: any) => ({ ...prev, source: nextNumPages }));
                            }}
                            className={'flex justify-center items-center h-full pointer-events-none'}>
                            {numPages.source > 0 ? (
                              <Page
                                className={'border border-solid border-gray-400'}
                                height={HEIGHT}
                                width={WIDTH}
                                pageNumber={pageMatch.source_page}
                                error={<FailedToLoadPage width={WIDTH} height={HEIGHT} />}
                              />
                            ) : null}
                          </Document>
                        </div>
                        <div
                          style={{ width: WIDTH, cursor: 'pointer' }}
                          onClick={() => {
                            setDetailMatching({ entity: selectedMatching, page: pageMatch.source_page });
                          }}>
                          {pageMatch.match_page ? (
                            <Document
                              file={documents.find(file => file.id === selectedMatching.match_id)?.pdf}
                              loading={<Spin />}
                              className={'flex justify-center items-center h-full pointer-events-none'}
                              onLoadSuccess={({ numPages: nextNumPages }: any) => {
                                setNumPages((prev: any) => ({ ...prev, match: nextNumPages }));
                              }}>
                              {numPages.match > 0 ? (
                                <Page
                                  className={'border border-solid border-gray-400'}
                                  height={HEIGHT}
                                  width={WIDTH}
                                  pageNumber={pageMatch.match_page}
                                  error={<FailedToLoadPage width={WIDTH} height={HEIGHT} />}
                                />
                              ) : null}
                            </Document>
                          ) : (
                            <div style={{ textAlign: 'center' }}>No matching page found</div>
                          )}
                        </div>
                      </Space>
                    </div>
                  </div>
                </Col>
                <Col
                  xs={{ span: 8 }}
                  xl={{ span: 6 }}
                  className="p-4 flex flex-col"
                  style={{
                    background: '#fff',
                    borderLeft: '1px solid #dcdcdc',
                    borderBottom: '1px solid #dcdcdc',
                  }}>
                  <p style={{ fontWeight: 'bold', fontSize: '16px' }}>Page {pageMatch.source_page}</p>
                  <div className="max-w-full overflow-x-scroll">
                    <Descriptions style={{ marginTop: '16px', width: '240px' }} column={1} bordered size="small">
                      <Descriptions.Item label="Signature">
                        {pageMatch.info.signed ? 'True' : 'False'}
                      </Descriptions.Item>
                    </Descriptions>
                  </div>
                </Col>
              </Row>
            );
          })}
        </>
      )}
    </>
  );
};
